import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

export type OnboardingInviteStatus = 'invited' | 'applied' | 'cancelled';
/** 'chain'/'franchisee' are legacy values from before those got a dedicated
 * shared-link flow — every new invite is 'independent'. */
export type BusinessCategory = 'chain' | 'franchisee' | 'independent';

export interface OnboardingInviteRow {
  id: string;
  token: string;
  business_category: BusinessCategory;
  email: string;
  contact_name: string | null;
  notes: string | null;
  status: OnboardingInviteStatus;
  invited_by_email: string | null;
  invited_at: string;
  resend_count: number;
  last_sent_at: string | null;
  matched_request_id: string | null;
  matched_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useOnboardingInvites(status: OnboardingInviteStatus | 'all' = 'invited') {
  return useQuery<{ ok: true; count: number; rows: OnboardingInviteRow[] }>({
    queryKey: ['admin', 'onboarding-invites', status],
    queryFn: () => adminFetch(`admin-list-onboarding-invites?status=${status}`),
    staleTime: 15 * 1000,
  });
}

export function useCreateOnboardingInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; contact_name?: string; notes?: string }) =>
      adminFetch<{ ok: true; invite: OnboardingInviteRow; email_sent: boolean; email_error: string | null }>(
        'admin-create-onboarding-invite',
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'onboarding-invites'] }),
  });
}

export function useResendOnboardingInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ ok: true }>('admin-resend-onboarding-invite', {
        method: 'POST',
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'onboarding-invites'] }),
  });
}

export function useCancelOnboardingInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ ok: true }>('admin-cancel-onboarding-invite', {
        method: 'POST',
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'onboarding-invites'] }),
  });
}
